// Google Analytics 4 — lazy-loaded, no-op when REACT_APP_GA_ID is empty.
//
// Why a hook? Create-React-App + react-router-dom doesn't auto-trigger GA on
// route changes (it's a SPA — the page never reloads). We listen for
// useLocation() changes and fire a pageview on each transition.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_ID;
let _loaded = false;

function loadGtag() {
  if (_loaded || !GA_ID || typeof window === "undefined") return;
  _loaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // Disable automatic page_view — we send our own on every route change so
  // SPA transitions are tracked correctly.
  window.gtag("config", GA_ID, { send_page_view: false });
}

export function trackPageView(path) {
  if (!GA_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!GA_ID || !window.gtag) return;
  window.gtag("event", name, params);
}

export function isAnalyticsEnabled() {
  return Boolean(GA_ID);
}

/** Hook — call once near the app root. Loads gtag, fires pageview on each route change. */
export function usePageView() {
  const location = useLocation();
  useEffect(() => {
    loadGtag();
  }, []);
  useEffect(() => {
    if (!GA_ID) return;
    const path = location.pathname + location.search;
    trackPageView(path);
  }, [location.pathname, location.search]);
}
